<template>
  <or-modal
    ref="modal"
    :is-open="isOpen"
    :size="ModalSize.S"
    data-component="base-create-layout-popup"
    @close="$emit('cancel')"
  >
    <template #header>
      {{ $t('HITL.ui-customization.create-layout-popup.title') }}
    </template>

    <div class="base-create-layout-popup-content">
      <or-input
        data-input="create-layout-popup-name"
        :model-value="values.name"
        :label="$t('HITL.ui-customization.create-layout-popup.layout-name-title')"
        :placeholder="$t('HITL.ui-customization.create-layout-popup.layout-name-placeholder')"
        :error="nameErrorMessage"
        @update:modelValue="onInput('name', $event)"
      />

      <or-select
        data-select="create-layout-popup-initial-component"
        :model-value="values.component"
        :label="$t('HITL.ui-customization.create-layout-popup.initial-component-title')"
        :placeholder="$t('HITL.ui-customization.create-layout-popup.initial-component-placeholder')"
        :options="componentOptions"
        :error="componentErrorMessage"
        @update:modelValue="onInput('component', $event)"
      />

      <or-textarea
        :model-value="values.description"
        :label="$t('HITL.ui-customization.create-layout-popup.description-title')"
        :placeholder="$t('HITL.ui-customization.create-layout-popup.description-placeholder')"
        @update:modelValue="onInput('description', $event)"
      />
    </div>

    <div class="base-create-layout-popup-footer">
      <or-checkbox
        data-checkbox="create-layout-popup-is-default"
        :model-value="values.makeActive"
        :label="$t('HITL.ui-customization.create-layout-popup.make-default')"
        @update:modelValue="onInput('makeActive', $event)"
      />

      <div class="base-create-layout-popup-footer-actions">
        <or-button
          data-button="cancel-create-layout"
          :variant="ButtonVariant.Outlined"
          @click="$emit('cancel')"
        >
          {{ $t('HITL.common.actions.cancel') }}
        </or-button>

        <or-button
          data-button="confirm-create-layout"
          @click="onCreate"
        >
          {{ $t('HITL.common.actions.create') }}
        </or-button>
      </div>
    </div>
  </or-modal>
</template>

<script lang="ts">
import { v4 } from 'uuid';
import {
  defineComponent,
  PropType,
  toRefs,
  ref,
  computed,
  getCurrentInstance,
} from 'vue-demi';
import {
  ModalSize,
  ButtonVariant,
  OrButtonV3 as OrButton,
  OrModalV3 as OrModal,
  OrInputV3 as OrInput,
  OrTextareaV3 as OrTextarea,
  OrSelectV3 as OrSelect,
  OrCheckboxV3 as OrCheckbox,
} from '@onereach/ui-components-25';
import { IFlexibleLayoutComponents } from '@onereach/grid-layout';
import { NON_EMPTY_INPUT_RE, FL_COLUMNS, FL_ROWS } from '@/constants';

export default defineComponent({
  name: 'BaseCreateLayoutPopup',
  components: {
    OrButton,
    OrModal,
    OrTextarea,
    OrInput,
    OrSelect,
    OrCheckbox,
  },
  props: {
    components: {
      type: Object as PropType<IFlexibleLayoutComponents>,
      default: () => new Object(),
    },
    isOpen: Boolean,
    isAgentApp: Boolean,
  },
  emits: ['create', 'cancel'],
  setup(props) {
    const { components, isAgentApp } = toRefs(props);
    const vm = getCurrentInstance()!;

    const values = ref({
      name: '',
      component: '',
      description: '',
      makeActive: true,
    });

    const touched = ref({
      name: false,
      component: false,
      description: false,
      makeActive: false,
    });

    const errors = computed(() => ({
      name: !NON_EMPTY_INPUT_RE.test(values.value.name),
      component: !NON_EMPTY_INPUT_RE.test(values.value.component),
    }));

    const nameErrorMessage = computed(() => {
      return touched.value.name && errors.value.name
        ? vm.proxy.$t('HITL.common.errors.property-required')
        : '';
    });

    const componentErrorMessage = computed(() => {
      return touched.value.component && errors.value.component
        ? vm.proxy.$t('HITL.common.errors.property-required')
        : '';
    });

    const componentOptions = computed(() => {
      return Object.values(components.value).map(({ key, label }) => ({
        value: key,
        label,
      }));
    });

    const isValid = computed(() => {
      return !errors.value.name && !errors.value.component;
    });

    const onInput = (key: keyof typeof values.value, value: string | boolean) => {
      touched.value[key] = true;
      values.value = { ...values.value, [key]: value };
    };

    const onCreate = () => {
      touched.value = {
        name: true,
        component: true,
        description: true,
        makeActive: true,
      };

      if (isValid.value) {
        const componentKey = values.value.component;
        const newLayout = {
          key: v4(),
          name: values.value.name,
          description: values.value.description,
          createdAt: new Date(),
          bookmarkedAt: null,
          madeByAgent: isAgentApp.value,
          columns: FL_COLUMNS,
          rows: FL_ROWS,
          items: {
            [componentKey]: {
              ...components.value[componentKey],
              column: 0,
              row: 0,
              width: FL_COLUMNS,
              height: FL_ROWS,
            },
          },
        };
        vm.proxy.$emit('create', {
          newLayout,
          meta: { makeActive: values.value.makeActive },
        });
      }
    };

    return {
      ButtonVariant,
      ModalSize,
      values,
      touched,
      errors,
      nameErrorMessage,
      componentErrorMessage,
      componentOptions,
      isValid,
      onInput,
      onCreate,
    };
  },
});
</script>

<style lang="scss" scoped>
[data-component='base-create-layout-popup'] {
  .base-create-layout-popup-content {
    display: flex;
    flex-direction: column;
    gap: var(--s-4);
  }

  .base-create-layout-popup-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: var(--s-5);

    &-actions {
      display: flex;
      gap: var(--s-4);
    }
  }
}
</style>
